import { useState, useEffect, useRef, type CSSProperties } from "react";
import type { EntityTypeDefinition } from "../../../lib/types";
import { useUpdateEntityType, useUpdateNode } from "../../../lib/hooks/use-kuvio";
import { showToast } from "../primitives";

interface RenameTableModalProps {
  open: boolean;
  onClose: () => void;
  entityType: EntityTypeDefinition;
  /** Library node id for this table — renamed alongside the entity type so the
   *  page header + breadcrumb reflect the change (the rail reads the type name). */
  tableNodeId: string;
  onRenamed?: () => void;
}

/** Lightweight pluraliser for the auto-derived plural; user can override. */
function autoPlural(name: string): string {
  const n = name.trim();
  if (!n) return "";
  if (/[^aeiou]y$/i.test(n)) return n.slice(0, -1) + "ies";
  if (/(s|x|z|ch|sh)$/i.test(n)) return n + "es";
  return n + "s";
}

export function RenameTableModal({ open, onClose, entityType, tableNodeId, onRenamed }: RenameTableModalProps) {
  const [name, setName] = useState("");
  const [plural, setPlural] = useState("");
  const [editPlural, setEditPlural] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const updateEntityType = useUpdateEntityType();
  const updateNode = useUpdateNode();

  useEffect(() => {
    if (open) {
      setName(entityType.name);
      setPlural(entityType.plural ?? autoPlural(entityType.name));
      setEditPlural(false);
      setError(null);
      setTimeout(() => nameRef.current?.focus(), 50);
    }
  }, [open, entityType.name, entityType.plural]);

  if (!open) return null;

  const pending = updateEntityType.isPending || updateNode.isPending;
  const effectivePlural = editPlural ? plural : autoPlural(name);

  async function handleSave() {
    const trimmed = name.trim();
    if (!trimmed) {
      setError("Name is required.");
      return;
    }
    setError(null);
    const finalPlural = (effectivePlural || "").trim() || autoPlural(trimmed);
    try {
      await updateEntityType.mutateAsync({
        slug: entityType.slug,
        patch: { name: trimmed, plural: finalPlural },
        expectedVersion: entityType._version,
      });
      await updateNode.mutateAsync({ id: tableNodeId, name: trimmed });
      showToast("Table renamed");
      onRenamed?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Rename failed");
    }
  }

  const overlay: CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 100,
    fontFamily: "var(--font-sans)",
  };
  const modal: CSSProperties = {
    background: "var(--color-background-primary)",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-lg)",
    padding: 24,
    width: 420,
    maxWidth: "90vw",
  };
  const labelStyle: CSSProperties = {
    display: "block",
    fontSize: 12,
    fontWeight: 500,
    color: "var(--color-text-secondary)",
    marginBottom: 4,
  };
  const inputStyle: CSSProperties = {
    width: "100%",
    fontSize: 13,
    padding: "8px 10px",
    border: "var(--kuvio-border-default)",
    borderRadius: "var(--border-radius-sm)",
    background: "var(--color-background-primary)",
    color: "var(--color-text-primary)",
    fontFamily: "var(--font-sans)",
    boxSizing: "border-box",
  };

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && !pending && onClose()}>
      <div
        style={modal}
        role="dialog"
        aria-label="Rename table"
        onKeyDown={(e) => {
          if (e.key === "Escape" && !pending) onClose();
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 500, color: "var(--color-text-primary)", marginBottom: 16 }}>
          Rename table
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Name</label>
          <input
            ref={nameRef}
            type="text"
            style={inputStyle}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleSave();
              }
            }}
            placeholder="e.g. Customer"
          />
        </div>

        <div style={{ marginBottom: 16 }}>
          {editPlural ? (
            <>
              <label style={labelStyle}>Plural</label>
              <input
                type="text"
                style={inputStyle}
                value={plural}
                onChange={(e) => setPlural(e.target.value)}
                placeholder="e.g. Customers"
              />
            </>
          ) : (
            <div style={{ fontSize: 12, color: "var(--color-text-tertiary)" }}>
              Plural: <span style={{ color: "var(--color-text-secondary)" }}>{effectivePlural || "—"}</span>{" "}
              <button
                type="button"
                onClick={() => {
                  setPlural(effectivePlural);
                  setEditPlural(true);
                }}
                style={{ background: "none", border: "none", color: "var(--color-text-info)", cursor: "pointer", fontSize: 12, padding: 0 }}
              >
                Edit plural
              </button>
            </div>
          )}
        </div>

        {error && <div style={{ fontSize: 12, color: "var(--c-red-600)", marginBottom: 12 }}>{error}</div>}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
          <button type="button" onClick={onClose} disabled={pending} style={{ fontSize: 13, padding: "8px 14px" }}>
            Cancel
          </button>
          <button
            type="button"
            data-variant="primary"
            onClick={handleSave}
            disabled={!name.trim() || pending}
            style={{ fontSize: 13, padding: "8px 14px" }}
          >
            {pending ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
